import type { KbPage, SeedPageSchema } from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";

function hasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

/** 与 seedPageFillStats / ProgressStats 同一口径：非空即已补全。 */
export function isSeedFieldFilled(value: unknown): boolean {
  return hasValue(value);
}

/** 种子表单字段标题旁状态：异常 > 已补全 > 未补全。 */
export type SeedFieldStatus = "anomaly" | "filled" | "empty";

/**
 * 计算字段标题旁图标状态。
 * `anomaly` 为非空说明时优先为橙色异常（即使已有值）。
 */
export function seedFieldStatus(
  value: unknown,
  anomaly?: string | null,
): SeedFieldStatus {
  if (anomaly != null && String(anomaly).trim().length > 0) return "anomaly";
  if (isSeedFieldFilled(value)) return "filled";
  return "empty";
}

/**
 * 从 page.frontmatter 读取字段级异常说明（预留 API）。
 * 支持 `field_flags` / `field_anomalies`，或 `meta.field_flags`；
 * 值为非空字符串的键才会进入结果。后端尚未写入时返回空对象。
 */
export function seedFieldAnomaliesFromFrontmatter(
  frontmatter: Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!frontmatter) return {};
  const meta =
    frontmatter.meta && typeof frontmatter.meta === "object" && !Array.isArray(frontmatter.meta)
      ? (frontmatter.meta as Record<string, unknown>)
      : null;
  const raw = frontmatter.field_flags ?? frontmatter.field_anomalies ?? meta?.field_flags;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && value.trim().length > 0) {
      out[key] = value.trim();
      continue;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const message =
        (value as { message?: unknown; reason?: unknown }).message ??
        (value as { message?: unknown; reason?: unknown }).reason;
      if (typeof message === "string" && message.trim().length > 0) {
        out[key] = message.trim();
      }
    }
  }
  return out;
}

/** Seed / page with no structured facts and no body — treat as empty for「待填写」CTA. */
export function isKbPageEmpty(page: KbPage): boolean {
  const structured = page.structured ?? {};
  const hasStructured = Object.values(structured).some(hasValue);
  const hasBody = (page.body ?? "").trim().length > 0;
  return !hasStructured && !hasBody;
}

export function emptySeedPages(pages: KbPage[]): KbPage[] {
  return pages.filter((page) => page.is_seed && isKbPageEmpty(page));
}

/**
 * 单条种子页相对 seed_schema.properties 的填写进度。
 * 与服务端 Kb::ProgressStats#schema_field_counts 同一口径：按 schema 键计 filled/total。
 * 侧栏徽章展示「已完成百分比」= round(filled / total * 100)；100% 时文案为「已完成」。
 */
export type SeedPageFillStats = {
  filled: number;
  total: number;
  /** 已填写比例 0–100（取整） */
  completedPercent: number;
  complete: boolean;
};

export function seedPageFillStats(
  page: KbPage,
  pageDef: SeedPageSchema | undefined | null,
): SeedPageFillStats {
  const keys = pageDef?.properties ? Object.keys(pageDef.properties) : [];

  // 无 schema 属性时退化为「整页空/非空」布尔进度，避免侧栏无徽章
  if (keys.length === 0) {
    const empty = isKbPageEmpty(page);
    return {
      filled: empty ? 0 : 1,
      total: 1,
      completedPercent: empty ? 0 : 100,
      complete: !empty,
    };
  }

  const structured = page.structured ?? {};
  let filled = 0;
  for (const key of keys) {
    if (hasValue(structured[key])) filled += 1;
  }
  const completedPercent = Math.round((filled / keys.length) * 100);
  return {
    filled,
    total: keys.length,
    completedPercent,
    complete: completedPercent === 100,
  };
}

/** 侧栏种子行徽章文案：未完成 →「已完成 N%」；完成 →「已完成」。 */
export function seedPageStatusBadgeLabel(stats: SeedPageFillStats): string {
  if (stats.complete) return KB_COPY.seedCompleteBadge;
  return `${KB_COPY.seedCompleteBadge} ${stats.completedPercent}%`;
}
